import { captureException } from '@sentry/browser';

import { calculateAvailabilityAndLeadTime } from './helpers/availability-lead-time';
import { fetchConfig } from './helpers/fetch';
import { trapFocus, removeTrapFocus } from './shared/utils/focus';
import { serializeForm } from './helpers/serialization';
import { getCartApiErrorMessage } from './helpers/utils.js';

class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.debouncedFetch = this.debounce(this._performFetch.bind(this), 400);
  }

  connectedCallback() {
    this.abortController = new AbortController();
    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close(), {
      signal: this.abortController.signal,
    });

    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this), { signal: this.abortController.signal })
    );
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  debounce = (func, delay) => {
    let timeoutId;

    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  };

  handleShippingCountryChange(event) {
    const localizationForm = document.querySelector('form.localization-form');

    if (localizationForm) {
      const formInput = localizationForm.querySelector('[name="country_code"]');
      const returnInput = localizationForm.querySelector('[name="return_to"]');

      if (formInput && returnInput) {
        formInput.value = event.target.value;
        returnInput.value = '/cart';

        localizationForm.submit();
      }
    }
  }

  handleQuantityChange(event) {
    const { target } = event;

    if (target.value === '0') {
      target.value = 1;
    }

    const { index } = target.dataset;
    const productId = target.dataset.product_id;
    const sections = ['cart-icon-bubble', 'cart-notification-button', 'cart-notification-product'];
    const buttonMinusEl = this.querySelector('.quantity__button[name=minus]');
    const buttonPlusEl = this.querySelector('.quantity__button[name=plus]');
    const loaderEl = this.querySelector('.cart-item__loading');

    buttonMinusEl.querySelectorAll('span')[1].classList.toggle('hidden', target.value <= 1);
    buttonMinusEl.disabled = target.value <= 1;

    const { leadTimeArray, inventoryQuantity, inventoryPolicy, useUp } = JSON.parse(
      target.closest('.cart-notification-product-table__product').dataset.availabilityLeadTime
    );

    const { availabilityStatus, leadTime } = calculateAvailabilityAndLeadTime(
      leadTimeArray,
      inventoryQuantity,
      inventoryPolicy,
      target.value,
      useUp
    );

    const body = JSON.stringify({
      line: index,
      quantity: target.value,
      sections,
      sections_url: window.location.pathname,
      lead_time: leadTime,
      availability: availabilityStatus,
    });

    this.debouncedFetch(body, sections, productId, loaderEl, target, buttonMinusEl, buttonPlusEl);
  }

  _performFetch(body, sections, productId, loaderEl, qtyInput, buttonMinusEl, buttonPlusEl) {
    qtyInput.disabled = true;
    buttonMinusEl.disabled = true;
    buttonPlusEl.disabled = true;

    loaderEl.classList.remove('hidden');

    fetch(`${window.routes.cart_change_url}`, { ...fetchConfig(), body, signal: this.abortController.signal })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);

        // /cart/change.js returns an error payload without sections when the change is
        // rejected (e.g. stock limit reached); the inputs are re-enabled in finally.
        if (!parsedState.sections) {
          this.setErrorMessage(getCartApiErrorMessage(parsedState, window.cartStrings.error));
          this.open(true);

          return;
        }

        this.clearErrorMessage();
        this._dispatchUpatedCart(parsedState, parsedState.items?.find((item) => item.id === this.productId)?.quantity);

        sections.forEach((section) => {
          const content = parsedState.sections[section];

          if (section === 'cart-notification-product') {
            const productHTML = this.getSectionInnerHTML(content, `#${section}-${productId}`);
            const totalPrice = this.getSectionInnerHTML(productHTML, '.cart-item__total .with-vat');
            const totalPriceExclVAT = this.getSectionInnerHTML(productHTML, '.cart-item__total .excl-vat');
            const cartTotal = this.getSectionInnerHTML(productHTML, '.cart-notification__links__cart-total');
            const deliveryEstimation = this.getSectionInnerHTML(productHTML, 'cart-delivery-estimation');

            if (totalPrice) {
              this.querySelector('.cart-item__total .with-vat').innerHTML = totalPrice;
            }

            this.querySelector('.cart-notification__links__cart-total').innerHTML = cartTotal;
            this.querySelector('.cart-item__total .excl-vat').innerHTML = totalPriceExclVAT;
            this.querySelector('cart-delivery-estimation').innerHTML = deliveryEstimation;
          } else {
            document.getElementById(section).innerHTML = content;
          }
        });
      })
      .catch((e) => {
        if (e.name === 'AbortError') {
          return;
        }

        throw e;
      })
      .finally(() => {
        qtyInput.disabled = false;
        buttonMinusEl.disabled = false;
        buttonPlusEl.disabled = false;
        loaderEl.classList.add('hidden');
      });
  }

  open(hasError = false) {
    const errorClass = 'cart-notification--has-error';

    document.body.style.overflow = 'hidden';

    this.notification.addEventListener('click', this.onBodyClick, { signal: this.abortController.signal });

    if (hasError) {
      this.notification.classList.add(errorClass);
    } else {
      this.notification.classList.remove(errorClass);
    }

    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true, signal: this.abortController.signal }
    );

    const shippingRateSelect = this.querySelector('[data-cart-country]');

    if (shippingRateSelect) {
      shippingRateSelect.addEventListener('change', this.handleShippingCountryChange, {
        signal: this.abortController.signal,
      });
    }

    const continueShoppingButton = this.querySelector('.cart-notification__links-continue');

    if (continueShoppingButton) {
      continueShoppingButton.addEventListener('click', this.close.bind(this), { signal: this.abortController.signal });
    }

    const accessoriesSection = this.querySelector('#accessories');

    if (accessoriesSection) {
      this.loadProductAccessories();
    }

    const qtyInput = this.querySelector('.quantity__input');

    if (qtyInput) {
      qtyInput.addEventListener('change', this.handleQuantityChange.bind(this), {
        signal: this.abortController.signal,
      });
    }
  }

  _dispatchUpatedCart(parsedState, productQuantity) {
    const thisProductSection = this._getSection(
      parsedState.sections['cart-notification-product'],
      `#cart-notification-product-${this.productId}`
    );

    const cartTotalElement = thisProductSection?.querySelector('.cart-notification__links__cart-total .total');

    if (!cartTotalElement) {
      return;
    }

    const cartTotalExclVAT = Number(cartTotalElement.innerText.replace(/\D/g, ''));

    document
      .getElementById('cart-icon-bubble')
      ?.dispatchEvent(
        new CustomEvent('cart-update', { detail: { productId: this.productId, productQuantity, cartTotalExclVAT } })
      );
  }

  close() {
    document
      .getElementById('cart-icon-bubble')
      .dispatchEvent(new CustomEvent('cart-notification-close', { bubbles: true }));
    this.notification.classList.remove('active');
    this.notification.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);

    document.getElementById('cart-notification-product').innerHTML = '';
    document.body.style.overflow = 'auto';
  }

  renderContents(parsedState) {
    // /cart/add.js returns an error payload without sections when the item cannot be added
    if (!parsedState.sections) {
      document.getElementById('cart-notification-product').innerHTML = '';
      this.setErrorMessage(getCartApiErrorMessage(parsedState, window.cartStrings.error));
      this.open(true);

      return;
    }

    this.productId = parsedState.id;
    this._dispatchUpatedCart(parsedState, parsedState.quantity);
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });
    this.open();
  }

  renderQuantityError(inventoryQuantity) {
    const inventoryQty = parseInt(inventoryQuantity, 10) || 0;

    this.setErrorMessage(window.cartStrings.quantityError.replace('[quantity]', inventoryQty));
    this.open(true);
  }

  setErrorMessage(errorMessage) {
    const errorMessageElem = this.notification.querySelector('[data-cart-notification-error-message]');

    if (errorMessageElem) {
      errorMessageElem.innerText = errorMessage;
    }

    this.notification.classList.add('cart-notification--has-error');
  }

  clearErrorMessage() {
    const errorMessageElem = this.notification.querySelector('[data-cart-notification-error-message]');

    if (errorMessageElem) {
      errorMessageElem.innerText = '';
    }

    this.notification.classList.remove('cart-notification--has-error');
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `#cart-notification-product-${this.productId}`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  _getSection(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const object = this._getSection(html, selector);

    if (object) {
      return object.innerHTML;
    }

    return null;
  }

  handleBodyClick(evt) {
    const { target } = evt;

    if (target.getAttribute('id') === 'cart-notification') {
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  loadProductAccessories() {
    const accessoriesSection = document.querySelector('#cart-notification #accessories');
    const relatedProductsSection = document.querySelector('.cart-notification__related-products');
    const accessoriesContainer = accessoriesSection.querySelector('[data-accessories-handles]');
    const productHandles = accessoriesContainer.dataset.accessoriesHandles;
    const noAvailableProducts = accessoriesContainer.dataset.noProducts;
    const productLimit = parseInt(accessoriesContainer?.dataset.limit, 10);
    const productHandlesArr = [...new Set(productHandles.split(','))];
    const allAccessoriesBtn = accessoriesSection.querySelector('.product-accessories__button-container');

    const productsFetchArr = productHandlesArr.map((handle) =>
      fetch(`${window.location.origin}/products/${handle}?view=accessories`, { signal: this.abortController.signal })
        .then((data) => data.ok && data.text())
        .then((response) => response)
    );

    if (productHandles === '') {
      accessoriesContainer.style.justifyContent = 'center';
      accessoriesContainer.innerHTML = `<p>${noAvailableProducts}</p>`;

      relatedProductsSection.remove();

      return;
    }

    Promise.all(productsFetchArr).then((results) => {
      const filteredResults = results.filter(Boolean).slice(0, productLimit);

      if (filteredResults.length === 0) {
        accessoriesContainer.style.justifyContent = 'center';
        accessoriesContainer.innerHTML = `<p>${noAvailableProducts}</p>`;

        relatedProductsSection.remove();

        return;
      }

      const filteredResultsString = filteredResults
        .map((el, index) => el.replace('{el.item}', `${index + 1}/${filteredResults.length}`))
        .join('');

      if (filteredResultsString.trim() === '') {
        accessoriesContainer.style.justifyContent = 'center';
        accessoriesContainer.innerHTML = `<p>${noAvailableProducts}</p>`;

        relatedProductsSection.remove();

        return;
      }

      accessoriesContainer.innerHTML = filteredResultsString;
      accessoriesContainer.querySelectorAll('[data-type="add-to-cart-form"]').forEach((form) => {
        form.addEventListener(
          'submit',
          (event) => {
            event.preventDefault();

            const accessoireForm = event.target;
            const submitButton = event.target.querySelector('[type="submit"]');

            submitButton.setAttribute('disabled', true);
            submitButton.classList.add('loading');

            const body = JSON.stringify({
              ...JSON.parse(serializeForm(accessoireForm)),
              sections: [],
              sections_url: window.location.pathname,
            });

            fetch(`${window.routes.cart_add_url}`, {
              ...fetchConfig('javascript'),
              body,
              signal: this.abortController.signal,
            })
              .then((response) => response.json())
              .catch((e) => {
                if (e.name === 'AbortError') {
                  return;
                }

                captureException(e);
              })
              .finally(() => {
                window.location.href = '/cart';
              });
          },
          { signal: this.abortController.signal }
        );
      });

      allAccessoriesBtn.style.visibility = 'visible';
    });
  }
}

if (!customElements.get('cart-notification')) {
  customElements.define('cart-notification', CartNotification);
}
